import React, { useMemo, useState } from "react";
import {
  openInvestigation,
  scheduleInvestigation,
  submitInvestigationDecision,
  updateInvestigation,
} from "../../api/investigations";
import { getApiErrorMessage } from "./investigationLabels";

function unique(ids) {
  return [...new Set((ids || []).filter(Boolean))];
}

function employeeHaystack(employee) {
  return [
    employee.name,
    employee.fullName,
    employee.code,
    employee.employeeCode,
    employee.jobTitle,
    employee.departmentName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function employeeLabel(employee) {
  if (!employee) return "";
  const name = employee.name || employee.fullName || "موظف";
  const title = employee.jobTitle ? ` — ${employee.jobTitle}` : "";
  const code = employee.code || employee.employeeCode;
  return code ? `${name}${title} (${code})` : `${name}${title}`;
}

function emptySlot(sessionDate = "", sessionStart = "", sessionEnd = "") {
  return { sessionDate, sessionStart, sessionEnd };
}

function fromParticipant(person, fallback = emptySlot()) {
  return {
    employeeId: person.employeeId,
    name: person.name,
    ...emptySlot(
      person.sessionDate || fallback.sessionDate || "",
      person.sessionStart || fallback.sessionStart || "",
      person.sessionEnd || fallback.sessionEnd || "",
    ),
  };
}

function EmployeeSearch({
  label,
  employees,
  excludeIds,
  query,
  onQueryChange,
  onPick,
  placeholder = "ابحث بالاسم أو الكود أو المسمى",
}) {
  const needle = query.trim().toLowerCase();
  const blocked = new Set((excludeIds || []).filter(Boolean));
  const visible = employees
    .filter((employee) => !blocked.has(employee.id))
    .filter((employee) => {
      if (!needle) return false;
      return employeeHaystack(employee).includes(needle);
    })
    .slice(0, 12);

  return (
    <div className="inv-field">
      <span>{label}</span>
      <input
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {needle ? (
        <div className="inv-people">
          {visible.length === 0 ? (
            <p className="inv-meta">لا توجد نتائج</p>
          ) : (
            visible.map((employee) => (
              <button
                key={employee.id}
                type="button"
                className="inv-person-pick"
                onClick={() => {
                  onPick(employee);
                  onQueryChange("");
                }}
              >
                {employeeLabel(employee)}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function ScheduleFields({ value, onChange }) {
  return (
    <div className="inv-form-grid inv-schedule-grid">
      <label className="inv-field">
        <span>يوم الحضور</span>
        <input
          type="date"
          value={value.sessionDate}
          onChange={(event) =>
            onChange({ ...value, sessionDate: event.target.value })
          }
          required
        />
      </label>
      <label className="inv-field">
        <span>من الساعة</span>
        <input
          type="time"
          value={value.sessionStart}
          onChange={(event) =>
            onChange({ ...value, sessionStart: event.target.value })
          }
          required
        />
      </label>
      <label className="inv-field">
        <span>إلى الساعة</span>
        <input
          type="time"
          value={value.sessionEnd}
          onChange={(event) =>
            onChange({ ...value, sessionEnd: event.target.value })
          }
          required
        />
      </label>
    </div>
  );
}

function AttendeeCard({ title, name, value, onChange, onRemove }) {
  return (
    <div className="inv-attendee-card">
      <div className="inv-attendee-card-top">
        <strong>{title ? `${title}: ${name}` : name}</strong>
        {onRemove ? (
          <button type="button" className="inv-btn" onClick={onRemove}>
            إزالة
          </button>
        ) : null}
      </div>
      <ScheduleFields value={value} onChange={onChange} />
    </div>
  );
}

export default function InvestigationEditor({
  mode,
  investigation,
  complaintId,
  complaintTitle,
  employees,
  investigatorDefault,
  onClose,
  onSaved,
}) {
  const activeEmployees = useMemo(
    () =>
      (employees || []).filter(
        (employee) => employee.status !== "terminated" && employee.id,
      ),
    [employees],
  );
  const employeeById = useMemo(() => {
    const map = new Map();
    activeEmployees.forEach((employee) => map.set(employee.id, employee));
    return map;
  }, [activeEmployees]);

  const [subjectQuery, setSubjectQuery] = useState("");
  const [partyQuery, setPartyQuery] = useState("");
  const [witnessQuery, setWitnessQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => {
    const fallback = emptySlot(
      investigation?.sessionDate || "",
      investigation?.sessionStart || "",
      investigation?.sessionEnd || "",
    );
    const subject = (investigation?.participants || []).find(
      (person) => person.kind === "SUBJECT" && person.employeeId,
    );
    return {
      incident: investigation?.incident || "",
      investigatorName:
        investigation?.investigatorName || investigatorDefault || "",
      subjectEmployeeId:
        investigation?.subjectEmployeeId || subject?.employeeId || "",
      subjectSchedule: subject
        ? fromParticipant(subject, fallback)
        : { ...fallback },
      parties: (investigation?.participants || [])
        .filter((person) => person.kind === "PARTY" && person.employeeId)
        .map((person) => fromParticipant(person, fallback)),
      witnesses: (investigation?.participants || [])
        .filter((person) => person.kind === "WITNESS" && person.employeeId)
        .map((person) => fromParticipant(person, fallback)),
      partiesText: investigation?.partiesText || "",
      witnessText: investigation?.witnessText || "",
      minutes: investigation?.minutes || "",
      result: investigation?.result || "",
      recommendations: investigation?.recommendations || "",
      decision: investigation?.decision || "NONE",
      decisionNotes: investigation?.decisionNotes || "",
      penaltyDays: investigation?.penaltyDays ?? "",
      penaltyAmount: investigation?.penaltyAmount ?? "",
    };
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function upsertList(key, employee, scheduleSeed) {
    setForm((prev) => {
      const list = prev[key];
      if (list.some((row) => row.employeeId === employee.id)) return prev;
      const seed =
        scheduleSeed ||
        prev.subjectSchedule ||
        emptySlot();
      return {
        ...prev,
        [key]: [
          ...list,
          {
            employeeId: employee.id,
            name: employee.name || employee.fullName,
            sessionDate: seed.sessionDate || "",
            sessionStart: seed.sessionStart || "",
            sessionEnd: seed.sessionEnd || "",
          },
        ],
      };
    });
  }

  function updateListItem(key, employeeId, nextSchedule) {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].map((row) =>
        row.employeeId === employeeId ? { ...row, ...nextSchedule } : row,
      ),
    }));
  }

  function removeListItem(key, employeeId) {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].filter((row) => row.employeeId !== employeeId),
    }));
  }

  function sessionPayload() {
    return {
      ...(mode === "create" && complaintId ? { complaintId } : {}),
      sessionDate: form.subjectSchedule.sessionDate,
      sessionStart: form.subjectSchedule.sessionStart,
      sessionEnd: form.subjectSchedule.sessionEnd,
      incident: form.incident.trim(),
      investigatorName: form.investigatorName.trim(),
      subjectEmployeeId: form.subjectEmployeeId,
      parties: form.parties.map((row) => ({
        employeeId: row.employeeId,
        sessionDate: row.sessionDate,
        sessionStart: row.sessionStart,
        sessionEnd: row.sessionEnd,
      })),
      witnesses: form.witnesses.map((row) => ({
        employeeId: row.employeeId,
        sessionDate: row.sessionDate,
        sessionStart: row.sessionStart,
        sessionEnd: row.sessionEnd,
      })),
      partiesText: form.partiesText.trim(),
      witnessText: form.witnessText.trim(),
    };
  }

  function validateSchedules() {
    if (!form.subjectEmployeeId) {
      return "حدد الموظف المطلوب حضوره";
    }
    if (
      !form.subjectSchedule.sessionDate ||
      !form.subjectSchedule.sessionStart ||
      !form.subjectSchedule.sessionEnd
    ) {
      return "حدد يوم ووقت حضور الموظف المطلوب";
    }
    for (const row of form.parties) {
      if (!row.sessionDate || !row.sessionStart || !row.sessionEnd) {
        return `حدد يوم ووقت حضور الطرف: ${row.name || "موظف"}`;
      }
    }
    for (const row of form.witnesses) {
      if (!row.sessionDate || !row.sessionStart || !row.sessionEnd) {
        return `حدد يوم ووقت حضور الشاهد: ${row.name || "موظف"}`;
      }
    }
    return "";
  }

  async function handleSessionSubmit(event) {
    event.preventDefault();
    const validationError = validateSchedules();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const payload = sessionPayload();
      const saved =
        mode === "schedule" && investigation?.id
          ? await scheduleInvestigation(investigation.id, payload)
          : mode === "edit" && investigation?.id
            ? await updateInvestigation(investigation.id, payload)
            : await openInvestigation(payload);
      onSaved(saved);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ التحقيق"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDecision(send) {
    setError("");
    if (!investigation?.id) return;
    setSaving(true);
    try {
      if (!send) {
        const saved = await updateInvestigation(investigation.id, {
          minutes: form.minutes,
          result: form.result,
          recommendations: form.recommendations,
        });
        onSaved(saved);
        return;
      }
      const payload = {
        minutes: form.minutes.trim(),
        result: form.result.trim(),
        recommendations: form.recommendations.trim(),
        decision: form.decision,
        decisionNotes: form.decisionNotes.trim(),
      };
      if (form.decision === "PENALTY") {
        if (form.penaltyDays !== "") payload.penaltyDays = Number(form.penaltyDays);
        if (form.penaltyAmount !== "") {
          payload.penaltyAmount = Number(form.penaltyAmount);
        }
      }
      const saved = await submitInvestigationDecision(investigation.id, payload);
      onSaved(saved);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إرسال القرار"));
    } finally {
      setSaving(false);
    }
  }

  const selectedSubject = employeeById.get(form.subjectEmployeeId);
  const excludeForParties = unique([
    form.subjectEmployeeId,
    ...form.parties.map((row) => row.employeeId),
    ...form.witnesses.map((row) => row.employeeId),
  ]);
  const excludeForWitnesses = excludeForParties;

  if (mode === "decision") {
    return (
      <form
        className="inv-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleDecision(true);
        }}
      >
        <div className="inv-detail-top">
          <h2>محضر التحقيق والقرار</h2>
          <button type="button" className="inv-btn" onClick={onClose}>
            إغلاق
          </button>
        </div>
        <label className="inv-field">
          <span>نص ما حدث في التحقيق</span>
          <textarea
            rows={6}
            value={form.minutes}
            onChange={(event) => setField("minutes", event.target.value)}
            required
          />
        </label>
        <label className="inv-field">
          <span>النتيجة</span>
          <textarea
            rows={3}
            value={form.result}
            onChange={(event) => setField("result", event.target.value)}
            required
          />
        </label>
        <label className="inv-field">
          <span>التوصيات</span>
          <textarea
            rows={3}
            value={form.recommendations}
            onChange={(event) => setField("recommendations", event.target.value)}
            required
          />
        </label>
        <div className="inv-choices">
          {[
            ["PENALTY", "جزاء"],
            ["WARNING", "لفت نظر"],
            ["NONE", "لا يستحق جزاء"],
          ].map(([value, label]) => (
            <label key={value} className="inv-choice">
              <input
                type="radio"
                name="decision"
                checked={form.decision === value}
                onChange={() => setField("decision", value)}
              />
              {label}
            </label>
          ))}
        </div>
        {form.decision === "PENALTY" && (
          <div className="inv-form-grid">
            <label className="inv-field">
              <span>أيام الخصم</span>
              <input
                type="number"
                min="0.25"
                max="30"
                step="0.25"
                value={form.penaltyDays}
                onChange={(event) => setField("penaltyDays", event.target.value)}
              />
            </label>
            <label className="inv-field">
              <span>مبلغ الجزاء</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.penaltyAmount}
                onChange={(event) =>
                  setField("penaltyAmount", event.target.value)
                }
              />
            </label>
          </div>
        )}
        {form.decision !== "NONE" && (
          <label className="inv-field">
            <span>
              {form.decision === "PENALTY" ? "تفاصيل الجزاء" : "نص لفت النظر"}
            </span>
            <textarea
              rows={3}
              value={form.decisionNotes}
              onChange={(event) => setField("decisionNotes", event.target.value)}
              required
            />
          </label>
        )}
        {error ? <p className="inv-error">{error}</p> : null}
        <div className="inv-form-actions">
          <button
            type="button"
            className="inv-btn"
            disabled={saving}
            onClick={() => handleDecision(false)}
          >
            حفظ المسودة
          </button>
          <button type="submit" className="inv-btn primary" disabled={saving}>
            إرسال للمدير العام
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="inv-form" onSubmit={handleSessionSubmit}>
      <div className="inv-detail-top">
        <h2>
          {mode === "schedule"
            ? "تحديد موعد التحقيق"
            : mode === "edit"
              ? "تعديل بيانات التحقيق"
              : "فتح تحقيق"}
        </h2>
        <button type="button" className="inv-btn" onClick={onClose}>
          إغلاق
        </button>
      </div>
      {complaintTitle ? (
        <p className="inv-meta">مرتبط بالشكوى: {complaintTitle}</p>
      ) : null}

      <label className="inv-field">
        <span>الواقعة</span>
        <textarea
          rows={4}
          value={form.incident}
          onChange={(event) => setField("incident", event.target.value)}
          required
        />
      </label>
      <label className="inv-field">
        <span>القائم بالتحقيق</span>
        <input
          value={form.investigatorName}
          onChange={(event) => setField("investigatorName", event.target.value)}
          required
        />
      </label>

      <div className="inv-field">
        <span>الموظف المطلوب حضوره</span>
        {selectedSubject || form.subjectEmployeeId ? (
          <AttendeeCard
            name={
              employeeLabel(selectedSubject) ||
              investigation?.subjectEmployeeName ||
              "موظف محدد"
            }
            value={form.subjectSchedule}
            onChange={(next) => setField("subjectSchedule", next)}
            onRemove={() => {
              setField("subjectEmployeeId", "");
              setField("subjectSchedule", emptySlot());
            }}
          />
        ) : (
          <EmployeeSearch
            label="بحث عن الموظف"
            employees={activeEmployees}
            excludeIds={[
              ...form.parties.map((row) => row.employeeId),
              ...form.witnesses.map((row) => row.employeeId),
            ]}
            query={subjectQuery}
            onQueryChange={setSubjectQuery}
            onPick={(employee) => {
              setField("subjectEmployeeId", employee.id);
              setField("subjectSchedule", {
                ...form.subjectSchedule,
              });
            }}
          />
        )}
      </div>

      <div className="inv-field">
        <span>الأطراف</span>
        <EmployeeSearch
          label="بحث لإضافة طرف"
          employees={activeEmployees}
          excludeIds={excludeForParties}
          query={partyQuery}
          onQueryChange={setPartyQuery}
          onPick={(employee) => upsertList("parties", employee)}
        />
        <div className="inv-attendee-list">
          {form.parties.map((row) => (
            <AttendeeCard
              key={row.employeeId}
              name={
                employeeLabel(employeeById.get(row.employeeId)) ||
                row.name ||
                "طرف"
              }
              value={row}
              onChange={(next) =>
                updateListItem("parties", row.employeeId, next)
              }
              onRemove={() => removeListItem("parties", row.employeeId)}
            />
          ))}
        </div>
      </div>
      <label className="inv-field">
        <span>نص الأطراف (اختياري، وإلا تُكتب الأسماء المختارة)</span>
        <textarea
          rows={2}
          value={form.partiesText}
          onChange={(event) => setField("partiesText", event.target.value)}
        />
      </label>

      <div className="inv-field">
        <span>الشهود</span>
        <EmployeeSearch
          label="بحث لإضافة شاهد"
          employees={activeEmployees}
          excludeIds={excludeForWitnesses}
          query={witnessQuery}
          onQueryChange={setWitnessQuery}
          onPick={(employee) => upsertList("witnesses", employee)}
        />
        <div className="inv-attendee-list">
          {form.witnesses.map((row) => (
            <AttendeeCard
              key={row.employeeId}
              name={
                employeeLabel(employeeById.get(row.employeeId)) ||
                row.name ||
                "شاهد"
              }
              value={row}
              onChange={(next) =>
                updateListItem("witnesses", row.employeeId, next)
              }
              onRemove={() => removeListItem("witnesses", row.employeeId)}
            />
          ))}
        </div>
      </div>
      <label className="inv-field">
        <span>نص الشاهد (اختياري)</span>
        <textarea
          rows={2}
          value={form.witnessText}
          onChange={(event) => setField("witnessText", event.target.value)}
        />
      </label>

      {error ? <p className="inv-error">{error}</p> : null}
      <div className="inv-form-actions">
        <button type="submit" className="inv-btn primary" disabled={saving}>
          {mode === "create"
            ? "فتح التحقيق وإرسال الدعوة"
            : mode === "schedule"
              ? "حفظ وإشعار الحضور"
              : "حفظ التعديل"}
        </button>
      </div>
    </form>
  );
}
